import { ReactChild } from "react"
import { create } from "zustand"

export interface Toast {
  id: number
  message: ReactChild
  type?: `success` | `warning` | `error` | `info`
  dismissed: boolean
  closable?: boolean
}

export interface NewToast {
  message: ReactChild | ((id: number) => ReactChild)
  type?: `success` | `warning` | `error` | `info`
  closable?: boolean
}

export interface ToastsState {
  toasts: Toast[]
}

export interface ToastsActions {
  dispatchToast: (details: NewToast, timeout?: number | false) => number
  dismissToast: (id: number) => void
  dismissAllToasts: () => void
}

let lastUsedId = 0

const useToasts = create<ToastsState & ToastsActions>((set, get) => ({
  toasts: [],
  dispatchToast: (
    { message, type = `success`, closable = true }: NewToast,
    dismissAfterMs: number | false = 5000
  ) => {
    const { dismissToast } = get()
    lastUsedId += 1
    const newToast: Toast = {
      id: lastUsedId,
      message: typeof message === `function` ? message(lastUsedId) : message,
      type,
      closable,
      dismissed: false
    }

    set(state => ({ toasts: state.toasts.concat(newToast) }))

    if (typeof dismissAfterMs === `number`) {
      setTimeout(() => {
        dismissToast(newToast.id)
      }, dismissAfterMs)
    }

    return newToast.id
  },
  dismissToast: (id: number) => {
    set(state => ({
      toasts: state.toasts.map(toast =>
        toast.id === id ? { ...toast, dismissed: true } : toast
      )
    }))
  },
  dismissAllToasts: () => {
    set(state => ({
      toasts: state.toasts.map(toast => ({ ...toast, dismissed: true }))
    }))
  }
}))

export default useToasts
